import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { SiPayModal } from 'utilities/components';
import { Button } from '30pay/components';
import { MdCameraswitch } from 'react-icons/md';

interface CameraCaptureModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCapture: (image: File) => void;
}

const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({ isOpen, onClose, onCapture }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [isCameraReady, setIsCameraReady] = useState<boolean>(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [capturedFile, setCapturedFile] = useState<File | null>(null);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');

    const handleCloseCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
            tracks.forEach((track) => track.stop());
            videoRef.current.srcObject = null;
        }
    };

    const startCamera = async (facing: 'user' | 'environment') => {
        try {
            const userMediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: facing },
            });
            if (videoRef.current) {
                videoRef.current.srcObject = userMediaStream;
                setIsCameraReady(true);
            }
        } catch (error) {
            console.error('Error accessing the camera: ', error);
            setIsCameraReady(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            startCamera(facingMode);
        } else {
            handleCloseCamera();
            setIsCameraReady(false);
            setCapturedImage(null);
            setCapturedFile(null);
        }
    }, [isOpen, facingMode]);

    const handleCapture = () => {
        if (canvasRef.current && videoRef.current) {
            const context = canvasRef.current.getContext('2d');
            if (context) {
                canvasRef.current.width = videoRef.current.videoWidth;
                canvasRef.current.height = videoRef.current.videoHeight;
                context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);

                canvasRef.current.toBlob((blob) => {
                    if (blob) {
                        const file = new File([blob], 'captured-image.png', { type: 'image/png' });
                        setCapturedImage(URL.createObjectURL(file));
                        setCapturedFile(file);
                    }
                }, 'image/png');

                setIsCameraReady(false);
            }
        }
        handleCloseCamera();
    };

    const handleConfirm = () => {
        if (capturedFile) {
            onCapture(capturedFile);
            onClose();
        }
    };

    const handleRetake = async () => {
        setCapturedImage(null);
        setIsCameraReady(false);
        startCamera(facingMode);
    };

    const switchCamera = () => {
        handleCloseCamera();
        setFacingMode((prevMode) => (prevMode === 'user' ? 'environment' : 'user'));
    };

    return (
        <SiPayModal isOpen={isOpen} onClose={onClose} title="عکس برداری">
            <div className="flex flex-col items-center">
                <Spin
                    className="w-full h-full absolute top-0 left-0 flex items-center justify-center z-0"
                    size="large"
                />
                <div className="z-10 relative">
                    {capturedImage ? (
                        <img src={capturedImage} alt="Captured" style={{ width: '100%', height: 'auto' }} />
                    ) : (
                        <div className="relative">
                            <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: 'auto' }} />
                            <Button
                                onClick={switchCamera}
                                className="absolute top-2 right-2 !w-auto min-w-8 min-h-8 max-w-8 max-h-8"
                                type="primary"
                                icon={<MdCameraswitch />}
                            />
                        </div>
                    )}
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>
                {capturedImage ? (
                    <div className="mt-4 gap-2 flex w-full">
                        <Button onClick={handleConfirm} type="primary" className="w-full">
                            تأیید
                        </Button>
                        <Button className="w-full" onClick={handleRetake} type="default">
                            دوباره
                        </Button>
                    </div>
                ) : (
                    <Button onClick={handleCapture} type="primary" className="mt-4 w-full" disabled={!isCameraReady}>
                        عکس برداری
                    </Button>
                )}
            </div>
        </SiPayModal>
    );
};

export default CameraCaptureModal;
